import type { OrderEvent } from '../api/booking/orderEventsApiSlice';
import type { CustomerMailBlockReason } from '../utils/customerMailGuard';

// "Has this customer ever heard from us about this booking?" — read off the
// append-only event log rather than asked of the operator.
//
// The operator is the wrong person to ask. A colleague may have taken the
// booking; a tenant may record phone bookings and confirm them verbally,
// pressing "Speichern" and never "Speichern und E-Mail senden". That is how a
// customer came to receive a cancellation notice as the first mail they had
// ever seen about a booking they did not know was recorded.
//
// `Order.is_mail_sent` cannot answer it: one boolean recording the LAST
// attempt, so a later mail overwrites an earlier one and a failure erases the
// memory of a success. The event log can, because nothing overwrites.

const SENT = 'communication.email.sent';
const FAILED = 'communication.email.failed';
const DUE_NOT_SENT = 'communication.email.due_not_sent';
// The payment rule refused the mail (PO, 2026-10-30): nothing was attempted,
// so it is neither a send nor a technical failure. `payload.code` names why.
const BLOCKED = 'communication.email.blocked';

const BLOCK_REASONS: readonly CustomerMailBlockReason[] = [
    'bank_details_missing',
    'stripe_not_connected',
    'payment_settings_missing',
];

/** `customer_mail_<reason>` → the reason, or null for a code the UI does not know. */
const blockReasonOf = (payload: OrderEvent['payload']): CustomerMailBlockReason | null => {
    const code = typeof payload?.code === 'string' ? payload.code : '';
    const reason = code.replace(/^customer_mail_/, '');
    return (BLOCK_REASONS as readonly string[]).includes(reason)
        ? (reason as CustomerMailBlockReason)
        : null;
};

/**
 * What the badge on booking details says. Three states, because there are
 * three (#1993) — one boolean was answering two different questions.
 *
 * `outdated` is the one that could not be expressed before: a booking whose
 * confirmation went out weeks ago and was then rescheduled without a mail read
 * as *sent*, which is true about the past and wrong about the present. The
 * customer turns up at the old time and nothing on screen ever said so.
 */
export type CustomerMailState = 'never' | 'current' | 'outdated';

export interface CustomerMailHistory {
    /** At least one customer mail about this booking is recorded as delivered. */
    hasBeenMailed: boolean;
    /** `occurredAt` of the most recent delivered mail, or null. */
    lastSentAt: string | null;
    sentCount: number;
    /** A mail was attempted for this booking and did not go out. */
    hasFailed: boolean;
    /**
     * `occurredAt` of the most recent customer-relevant change saved without
     * telling the customer, or null.
     */
    lastUncommunicatedAt: string | null;
    /** The three-state answer the badge renders. */
    state: CustomerMailState;
    /**
     * The payment rule refused the most recent mail asked for, and no mail
     * went out since: why (`communication.email.blocked`). `null` otherwise —
     * also for a refusal a later sent mail has made history.
     */
    blockedReason: CustomerMailBlockReason | null;
}

export const readCustomerMailHistory = (
    events: OrderEvent[] | undefined,
): CustomerMailHistory => {
    const sent = (events ?? []).filter((e) => e.eventType === SENT);
    const failed = (events ?? []).some((e) => e.eventType === FAILED);
    const uncommunicated = (events ?? []).filter(
        (e) => e.eventType === DUE_NOT_SENT,
    );

    // The endpoint returns occurred_at ascending, but the reader does not
    // depend on that: an event log gains writers (the monitoring cron, the
    // other backend) and an ordering assumption is the kind of thing that
    // quietly stops holding.
    const latestOf = (rows: OrderEvent[]): string | null =>
        rows.reduce<string | null>(
            (latest, e) => (latest === null || e.occurredAt > latest ? e.occurredAt : latest),
            null,
        );

    const lastSentAt = latestOf(sent);
    const lastUncommunicatedAt = latestOf(uncommunicated);
    const blocked = (events ?? []).filter((e) => e.eventType === BLOCKED);
    const lastBlocked = blocked.reduce<OrderEvent | null>(
        (latest, e) => (latest === null || e.occurredAt > latest.occurredAt ? e : latest),
        null,
    );
    const blockedReason =
        lastBlocked && (lastSentAt === null || lastBlocked.occurredAt > lastSentAt)
            ? blockReasonOf(lastBlocked.payload)
            : null;

    // "Never" wins over "outdated": if nothing was ever sent, that is both the
    // stronger warning and the truer one — there is no earlier mail for a
    // change to have fallen out of step with.
    //
    // Otherwise the two timestamps decide, and a later mail closes the gap on
    // its own. That is why the backend records the change rather than setting a
    // flag somebody would have to remember to clear.
    let state: CustomerMailState = 'never';
    if (sent.length > 0) {
        state =
            lastUncommunicatedAt !== null
            && lastSentAt !== null
            && lastUncommunicatedAt > lastSentAt
                ? 'outdated'
                : 'current';
    }

    return {
        hasBeenMailed: sent.length > 0,
        lastSentAt,
        sentCount: sent.length,
        hasFailed: failed,
        lastUncommunicatedAt,
        state,
        blockedReason,
    };
};

/**
 * The detail behind the badge, for the tooltip (#1993): when the last mail went
 * out, how many there were, and — in the `outdated` case — when the change
 * happened that nobody told the customer about.
 *
 * Returns a key plus interpolation values rather than a finished string,
 * because the dates have to be formatted in the reader's locale and only the
 * component knows it. Same split as `customerMailHint` below.
 *
 * `null` when there is nothing useful to say, so a failed events read renders
 * no tooltip rather than a confident sentence about data we do not have.
 */
export const customerMailTooltip = (
    history: CustomerMailHistory | null,
    formatDateTime: (iso: string) => string,
): { messageKey: string, values: Record<string, string | number> } | null => {
    if (history === null) return null;

    if (history.state === 'outdated'
        && history.lastSentAt !== null
        && history.lastUncommunicatedAt !== null) {
        return {
            messageKey: 'booking_details.mail_sent.tooltip_change_not_sent',
            values: {
                date: formatDateTime(history.lastSentAt),
                count: history.sentCount,
                changedAt: formatDateTime(history.lastUncommunicatedAt),
            },
        };
    }

    if (history.state === 'current' && history.lastSentAt !== null) {
        return {
            messageKey: 'booking_details.mail_sent.tooltip_sent',
            values: {
                date: formatDateTime(history.lastSentAt),
                count: history.sentCount,
            },
        };
    }

    // `never` — and also the shapes that cannot be described honestly (a state
    // whose timestamps are missing). Saying "no mail is recorded" is exactly
    // what the log knows in both cases.
    return {
        messageKey: 'booking_details.mail_sent.tooltip_none',
        values: { count: 0 },
    };
};

/**
 * The sentence the cancel dialog adds under its question.
 *
 * The wording says **protokolliert** ("recorded"), not "sent", and that is not
 * hedging — it is the only truthful phrasing. Bookings made before this log
 * existed carry no events, so "no mail was sent" would be a claim the data
 * does not support, while "no mail is recorded" is exactly what we know. As
 * the log fills the two converge; until then the operator is told what the
 * system knows and still makes the call.
 *
 * Returns null when there is nothing useful to say, so a failed or missing
 * events read degrades to the plain dialog rather than to a wrong claim.
 */
export const customerMailHint = (
    history: CustomerMailHistory | null,
): { messageKey: string, count: number } | null => {
    if (history === null) return null;

    if (history.hasBeenMailed) {
        return {
            messageKey: 'booking_details.confirmDialog.mail_history_sent',
            count: history.sentCount,
        };
    }
    // A booking whose only mail attempt FAILED is the sharpest case for
    // sending now: we meant to write to this customer and they never heard
    // from us, so the cancellation would still be their first contact.
    if (history.hasFailed) {
        return {
            messageKey: 'booking_details.confirmDialog.mail_history_failed',
            count: 0,
        };
    }
    return {
        messageKey: 'booking_details.confirmDialog.mail_history_none',
        count: 0,
    };
};
